/**
 * Unités de charge (kg / lbs) — source unique de vérité côté frontend.
 *
 * Modèle de données (décisions du 6 oct 2026) :
 *  - `profiles.weight_unit` : unité de l'utilisateur ('kg' | 'lbs', défaut 'kg').
 *    Pour un coach = l'unité dans laquelle il tape, celle de ses templates et
 *    l'unité par défaut de ses nouveaux élèves. Pour un élève = celle de sa salle.
 *  - Dans le JSONB `exercises` d'une séance : `exercise.unit` = unité dans laquelle
 *    les charges prescrites (`set.weight`) ont été saisies. Absent → 'kg'
 *    (toutes les données existantes sont en kg).
 *  - `set.student_weight_unit` = unité de la charge réelle saisie par l'élève.
 *    Absent → unité de l'exercice.
 *  - Les colonnes numériques (1RM, poids de corps, workout_videos.weight,
 *    ris_history) restent TOUJOURS en kg : conversion à la saisie et à l'affichage.
 *
 * Arrondi : une conversion croisée tombe sur ce qui se charge vraiment dans une
 * salle commerciale — multiple de 5 lbs (paire de 2,5) ou de 2,5 kg (paire de 1,25).
 * Jamais de réglage utilisateur (refusé : usine à gaz).
 * Une « conversion » vers la même unité ne touche jamais la valeur.
 */

export const KG = 'kg';
export const LBS = 'lbs';
export const UNITS = [KG, LBS];
export const DEFAULT_UNIT = KG;

export const KG_PER_LB = 0.45359237;
export const LB_PER_KG = 1 / KG_PER_LB;

/** Plus petit saut réaliste sur la barre, par unité. */
export const PLATE_STEP = { [KG]: 2.5, [LBS]: 5 };

/** Incrément de progression par défaut (panneau ⚙️), par unité. */
export const DEFAULT_INCREMENT = { [KG]: 2.5, [LBS]: 5 };

/** 'kg' | 'lbs' ; tout le reste (null, 'KG', 'lb', 'pounds'…) est normalisé. */
export function normalizeUnit(unit) {
  if (unit == null) return DEFAULT_UNIT;
  const u = String(unit).trim().toLowerCase();
  if (u === 'lb' || u === 'lbs' || u === 'pound' || u === 'pounds') return LBS;
  return KG;
}

export function isValidUnit(unit) {
  return unit === KG || unit === LBS;
}

/** Unité des charges prescrites d'un exercice (JSONB). */
export function getExerciseUnit(exercise) {
  return normalizeUnit(exercise?.unit);
}

/** Unité de la charge réelle d'une série (saisie élève). */
export function getStudentWeightUnit(set, exercise) {
  if (set?.student_weight_unit) return normalizeUnit(set.student_weight_unit);
  if (set?.studentWeightUnit) return normalizeUnit(set.studentWeightUnit);
  return getExerciseUnit(exercise);
}

/** Nombre tolérant (virgule FR, espaces). null si illisible ou non fini. */
export function parseNumber(value) {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const s = String(value).trim().replace(/\s/g, '').replace(',', '.');
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Conversion brute, sans arrondi. */
export function convertRaw(value, from, to) {
  const f = normalizeUnit(from);
  const t = normalizeUnit(to);
  if (f === t) return value;
  return f === KG ? value * LB_PER_KG : value * KG_PER_LB;
}

/** Arrondit au multiple du plus petit saut de l'unité (2,5 kg / 5 lbs). */
export function roundToPlate(value, unit) {
  const step = PLATE_STEP[normalizeUnit(unit)];
  return Math.round(value / step) * step;
}

/** Nettoie les flottants (0.1+0.2) sans imposer d'arrondi métier. */
function clean(n) {
  return Math.round(n * 1000) / 1000;
}

/**
 * Conversion d'une CHARGE entre unités.
 *  - même unité → valeur inchangée (jamais d'arrondi) ;
 *  - unité différente → convertie puis arrondie au disque (option `round`, défaut true).
 */
export function convertLoad(value, from, to, { round = true } = {}) {
  const n = parseNumber(value);
  if (n == null) return null;
  const f = normalizeUnit(from);
  const t = normalizeUnit(to);
  if (f === t) return n;
  const raw = convertRaw(n, f, t);
  return clean(round ? roundToPlate(raw, t) : raw);
}

/** Vers le kg (colonnes numériques, calculs). Pas d'arrondi disque : on garde la précision. */
export function toKg(value, unit) {
  return convertLoad(value, unit, KG, { round: false });
}

/** Depuis le kg (affichage d'une valeur stockée en kg). Arrondi disque si lbs. */
export function fromKg(valueKg, unit) {
  return convertLoad(valueKg, KG, unit);
}

/**
 * Convertit chaque nombre d'un TEXTE LIBRE de charge (« 40-45 », « 40 / 42,5 »,
 * « PDC », « +10 ») et laisse le reste intact. Même unité → texte rendu tel quel.
 * Un nombre immédiatement suivi de « % » n'est pas converti.
 */
export function convertLoadText(text, from, to) {
  if (text == null) return text;
  const f = normalizeUnit(from);
  const t = normalizeUnit(to);
  if (f === t) return text;
  const s = String(text);
  return s.replace(/(\d+(?:[.,]\d+)?)(\s*%)?/g, (m, num, pct) => {
    if (pct) return m;
    const converted = convertLoad(num, f, t);
    if (converted == null) return m;
    const decimalComma = num.includes(',');
    return formatNumber(converted, { decimalComma });
  });
}

/** Nombre → texte court : 90 → « 90 », 42.5 → « 42,5 » (virgule FR par défaut). */
export function formatNumber(n, { decimalComma = true, maxDecimals = 2 } = {}) {
  if (n == null || !Number.isFinite(n)) return '';
  const s = String(clean(Math.round(n * 10 ** maxDecimals) / 10 ** maxDecimals));
  return decimalComma ? s.replace('.', ',') : s;
}

/** Libellé court d'unité. */
export function unitLabel(unit) {
  return normalizeUnit(unit) === LBS ? 'lbs' : 'kg';
}

/**
 * Formate une charge avec son unité : formatLoad(42.5, 'kg') → « 42,5 kg ».
 * `value` peut être un texte libre ; s'il contient des lettres (« PDC »), pas de suffixe.
 * Options : `from` (unité de la valeur, défaut = `unit`) pour convertir à la volée,
 *           `space` (défaut true), `suffix` (défaut true).
 */
export function formatLoad(value, unit, { from, space = true, suffix = true } = {}) {
  if (value == null || value === '') return '';
  const to = normalizeUnit(unit);
  const converted = from == null ? value : convertLoadText(value, from, to);
  const n = parseNumber(converted);
  const text = n == null ? String(converted) : formatNumber(n);
  if (!suffix || /[a-zA-Z]/.test(text)) return text;
  return `${text}${space ? ' ' : ''}${unitLabel(to)}`;
}

/**
 * Aide affichée sous un champ quand le coach tape dans une unité et que l'élève
 * lit dans une autre. null si même unité ou valeur illisible.
 * Retourne { value, unit, raw, rounded } : `rounded` = true si l'arrondi disque a modifié la valeur.
 */
export function describeConversion(value, from, to) {
  const f = normalizeUnit(from);
  const t = normalizeUnit(to);
  if (f === t) return null;
  const n = parseNumber(value);
  if (n == null) return null;
  const raw = clean(convertRaw(n, f, t));
  const value2 = convertLoad(n, f, t);
  return { value: value2, unit: t, raw, rounded: Math.abs(raw - value2) > 0.001 };
}

/** Charge prescrite d'une série, exprimée dans `displayUnit` (texte libre respecté). */
export function displaySetWeight(set, exercise, displayUnit) {
  return convertLoadText(set?.weight, getExerciseUnit(exercise), displayUnit);
}

/** Charge réelle (élève) d'une série, exprimée dans `displayUnit`. */
export function displayStudentWeight(set, exercise, displayUnit) {
  const raw = set?.student_weight ?? set?.studentWeight;
  return convertLoadText(raw, getStudentWeightUnit(set, exercise), displayUnit);
}
